import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileText, Mic, Loader2, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { generateJDFromAudio, getAzureUploadUrl, uploadToAzureBlob, generateJDFromBlob, pollJDGenerationStatus, cancelJDGeneration } from '../../../../api/client';
import { convertMarkdownToHtml } from '../../../../utils/markdownToHTML';
import useCompanyData from '../../../../hooks/useCompanyData';

const ALLOWED_AUDIO_TYPES = ['.mp3', '.wav', '.m4a', '.ogg', '.webm'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const POLL_INTERVAL_MS = 4000; // poll every 4 seconds

export default function JDFromAudioModal({ open, onClose, onApply, initialAuditId = null }) {
  const { t } = useTranslation();
  const { company } = useCompanyData();
  const [activeTab, setActiveTab] = useState('audio');
  const [audioFile, setAudioFile] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [language, setLanguage] = useState('en');

  // status: idle | loadingDraft | uploading | processing | done | error
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100

  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const auditIdRef = useRef(null);
  const companyName = company?.name?.trim() || '';

  // Cleanup timers on unmount / close
  const stopTimers = useCallback(() => {
    clearInterval(pollTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    pollTimerRef.current = null;
    elapsedTimerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    stopTimers();
    setAudioFile(null);
    setTranscriptText('');
    setStatus('idle');
    setErrorMessage('');
    setResult(null);
    setShowTranscript(false);
    setDragActive(false);
    setElapsedSeconds(0);
    setUploadProgress(0);
    auditIdRef.current = null;
  }, [stopTimers]);

  const handleClose = () => { reset(); onClose(); };

  const fetchGenerationStatus = useCallback(async (auditId) => {
    const data = await pollJDGenerationStatus(auditId);

    if (data.status === 'done') {
      stopTimers();
      setResult(data);
      setStatus('done');
      return data;
    }

    if (data.status === 'error') {
      stopTimers();
      setErrorMessage(data.error_message || 'Generation failed.');
      setStatus('error');
      return data;
    }

    return data;
  }, [stopTimers]);

  // ── Start elapsed-time counter ──────────────────────────────────────────
  const startElapsedTimer = () => {
    setElapsedSeconds(0);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
  };

  // ── Poll for JD generation status ──────────────────────────────────────
  const startPolling = (auditId) => {
    auditIdRef.current = auditId;
    startElapsedTimer();
    pollTimerRef.current = setInterval(async () => {
      try {
        const data = await fetchGenerationStatus(auditId);
        if (data.status === 'done' || data.status === 'error') {
          return;
        }
      } catch {
        // Transient network error — keep polling
      }
    }, POLL_INTERVAL_MS);
  };

  // ── Resume polling for an existing audit ──────────────────────────────────
  useEffect(() => {
    if (!initialAuditId || !open) return undefined;

    let isActive = true;

    const resumeExistingDraft = async () => {
      auditIdRef.current = initialAuditId;
      setErrorMessage('');
      setResult(null);
      setStatus('loadingDraft');

      try {
        const data = await fetchGenerationStatus(initialAuditId);
        if (!isActive || !data) return;

        if (data.status !== 'done' && data.status !== 'error') {
          setStatus('processing');
          startPolling(initialAuditId);
        }
      } catch (err) {
        if (!isActive) return;
        const msg = err.response?.data?.error || err.message || 'Failed to load this JD draft.';
        setErrorMessage(msg);
        setStatus('error');
      }
    };

    resumeExistingDraft();

    return () => stopTimers();
  }, [fetchGenerationStatus, initialAuditId, open, stopTimers]);

  // ── File validation ──────────────────────────────────────────────────────
  const validateFile = (file) => {
    if (!file) return t('jobDescription.audioModal.errors.noFile');
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_AUDIO_TYPES.includes(ext)) return t('jobDescription.audioModal.errors.unsupportedFormat', { ext, allowed: ALLOWED_AUDIO_TYPES.join(', ') });
    if (file.size > MAX_FILE_SIZE) return t('jobDescription.audioModal.errors.fileTooLarge', { size: (file.size / (1024 * 1024)).toFixed(1) });
    return null;
  };

  const handleFileSelect = (file) => {
    const err = validateFile(file);
    if (err) { setErrorMessage(err); setStatus('error'); return; }
    setAudioFile(file);
    setErrorMessage('');
    setStatus('idle');
  };

  const handleDrop = (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);

  // ── Submit ──
  const handleGenerate = async () => {
    setStatus('uploading');
    setErrorMessage('');
    setUploadProgress(0);

    try {
      let data;

      if (activeTab === 'audio') {
        if (!audioFile) { setErrorMessage(t('jobDescription.audioModal.errors.noFile')); setStatus('error'); return; }

        // ── Try Azure Blob direct upload first ────────────────────────────
        const companyId = company?.id;
        if (companyId) {
          try {
            // Step 1: Get SAS URL from backend
            const { upload_url, blob_path } = await getAzureUploadUrl(
              audioFile.name, 'audio', companyId
            );

            // Step 2: Upload directly to Azure Blob (with progress)
            await uploadToAzureBlob(upload_url, audioFile, setUploadProgress);

            // Step 3: Tell backend to process (tiny JSON, instant)
            data = await generateJDFromBlob(blob_path, language, companyName);
          } catch (azureErr) {
            // If Azure isn't configured (503) or fails, fall back to legacy multipart
            const isAzureNotConfigured = azureErr.response?.status === 503;
            if (isAzureNotConfigured) {
              console.warn('Azure Blob not configured — falling back to multipart upload');
            } else {
              console.warn('Azure upload failed — falling back to multipart:', azureErr.message);
            }
            // Reset progress and try legacy path
            setUploadProgress(0);
            data = null;
          }
        }

        // ── Fallback: legacy multipart upload through Django ──────────────
        if (!data) {
          const formData = new FormData();
          formData.append('language', language);
          formData.append('audio_file', audioFile);
          if (companyName) {
            formData.append('company_name', companyName);
          }
          data = await generateJDFromAudio(formData, (e) => {
            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          });
        }
      } else {
        // Transcript tab — no file upload
        if (!transcriptText.trim()) { setErrorMessage(t('jobDescription.audioModal.errors.noTranscript')); setStatus('error'); return; }
        const formData = new FormData();
        formData.append('language', language);
        formData.append('transcript_text', transcriptText);
        if (companyName) {
          formData.append('company_name', companyName);
        }
        data = await generateJDFromAudio(formData);
      }

      // Server returned 202 immediately — now switch to polling
      setStatus('processing');
      setUploadProgress(100);
      startPolling(data.audit_id);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Something went wrong. Please try again.';
      setErrorMessage(msg);
      setStatus('error');
    }
  };

  const handleCancelProcess = async () => {
    if (!auditIdRef.current) {
      reset();
      return;
    }

    try {
      await cancelJDGeneration(auditIdRef.current);
    } catch (err) {
      console.error("Failed to cancel generation on the server", err);
    } finally {
      reset();
    }
  };

  // ── Apply result to parent form ──
  const handleApply = () => {
    if (!result?.structured_description) return;
    const sd = result.structured_description;
    let description = result.generated_description || '';
    if (sd.title) {
      const titleRegex = new RegExp(`^#{1,3}\\s*${sd.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gm');
      description = description.replace(titleRegex, '');
    }
    const htmlContent = convertMarkdownToHtml(description);
    onApply({
      title: sd.title || '',
      description: htmlContent,
      required_skills: sd.required_skills_list || [],
      nice_to_have_skills: sd.nice_to_have_skills_list || [],
      experience_level: sd.experience_level || '',
      work_type: sd.work_type || '',
      employment_type: sd.employment_type || '',
      salary_range_min: sd.salary_range_min || '',
      salary_range_max: sd.salary_range_max || '',
      location: sd.location || '',
    });
    handleClose();
  };

  if (!open) return null;

  const isProcessing = status === 'uploading' || status === 'processing';
  const isLoadingDraft = status === 'loadingDraft';
  const formatElapsed = (s) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  const previewCompanyName = result?.structured_description?.company_name || companyName;
  const processingLabel = {
    uploading: uploadProgress > 0
      ? t('jobDescription.audioModal.processing.uploading', { progress: uploadProgress })
      : t('jobDescription.audioModal.processing.preparing'),
    processing: activeTab === 'audio'
      ? t('jobDescription.audioModal.processing.transcribing', { elapsed: formatElapsed(elapsedSeconds) })
      : t('jobDescription.audioModal.processing.generating', { elapsed: formatElapsed(elapsedSeconds) }),
    loadingDraft: t('jobDescription.audioModal.processing.loadingDraft'),
  }[status] || '';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]">
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}>
              <Mic className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('jobDescription.audioModal.title')}</h2>
              <p className="text-sm text-gray-500">
                {initialAuditId ? t('jobDescription.audioModal.subtitleReview') : t('jobDescription.audioModal.subtitle')}
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={isProcessing} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Tabs (hide while processing/done) */}
          {!isProcessing && !isLoadingDraft && status !== 'done' && (
            <div className="flex bg-gray-100 rounded-xl p-1">
              {[
                ['audio', Upload, t('jobDescription.audioModal.tabs.audio')],
                ['transcript', FileText, t('jobDescription.audioModal.tabs.transcript')]
              ].map(([tab, Icon, label]) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setErrorMessage(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Icon size={16} /> {label}
                </button>
              ))}
            </div>
          )}

          {/* Input area (hide while processing/done) */}
          {!isProcessing && !isLoadingDraft && status !== 'done' && (
            <>
              {activeTab === 'audio' ? (
                <div>
                  <div
                    onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${dragActive ? 'border-purple-400 bg-purple-50' : audioFile ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-purple-300 hover:bg-purple-50/50'}`}
                  >
                    <input ref={fileInputRef} type="file" accept=".mp3,.wav,.m4a,.ogg,.webm" onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])} className="hidden" />
                    {audioFile ? (
                      <div className="space-y-2">
                        <CheckCircle className="mx-auto text-green-500" size={36} />
                        <p className="font-semibold text-green-700">{audioFile.name}</p>
                        <p className="text-sm text-green-600">{(audioFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                        <p className="text-xs text-gray-400">{t('jobDescription.audioModal.dropzone.clickToChange')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="mx-auto text-gray-400" size={36} />
                        <p className="font-semibold text-gray-600">{t('jobDescription.audioModal.dropzone.idle')}</p>
                        <p className="text-sm text-gray-400">{t('jobDescription.audioModal.dropzone.support')}</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('jobDescription.audioModal.languageLabel')}</label>
                    <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all bg-white">
                      {[
                        ['en', 'English'],
                        ['hi', 'Hindi'],
                        ['bn', 'Bengali'],
                        ['mr', 'Marathi'],
                        ['ar', 'Arabic'],
                        ['es', 'Spanish'],
                        ['fr', 'French'],
                        ['pt', 'Portuguese'],
                        ['zh', 'Chinese'],
                        ['ja', 'Japanese'],
                        ['ko', 'Korean'],
                        ['de', 'German'],
                        ['ta', 'Tamil'],
                        ['te', 'Telugu'],
                        ['kn', 'Kannada'],
                        ['gu', 'Gujarati'],
                        ['ml', 'Malayalam']
                      ].map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{t('jobDescription.audioModal.transcriptLabel')}</label>
                  <textarea value={transcriptText} onChange={e => setTranscriptText(e.target.value)} placeholder={t('jobDescription.audioModal.transcriptPlaceholder')} rows={8} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none bg-white" />
                  <p className="text-xs text-gray-400 mt-1">{transcriptText.length} characters</p>
                </div>
              )}
            </>
          )}

          {/* Error */}
          {status === 'error' && errorMessage && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={20} />
              <div><p className="text-sm font-semibold text-red-700">{t('jobDescription.statusFailed')}</p><p className="text-sm text-red-600 mt-0.5">{errorMessage}</p></div>
            </div>
          )}

          {isLoadingDraft && (
            <div className="flex flex-col items-center gap-4 py-14">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                  <FileText className="text-blue-600" size={30} />
                </div>
                <div className="absolute -inset-2 rounded-full border-4 border-blue-100 animate-pulse opacity-80" />
              </div>
              <div className="text-center max-w-md">
                <p className="font-bold text-gray-800 text-lg">{processingLabel}</p>
                <p className="text-sm text-gray-500 mt-2">{t('jobDescription.audioModal.processing.loadingDraft')}</p>
              </div>
            </div>
          )}

          {/* Processing state */}
          {isProcessing && (
            <div className="flex flex-col items-center gap-4 py-10">
              <div className="relative">
                <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}>
                  <Mic className="text-white" size={32} />
                </div>
                <div className="absolute -inset-2 rounded-full border-4 border-purple-200 animate-ping opacity-50" />
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-800 text-lg">{processingLabel}</p>
                {/* Upload Progress Bar */}
                {status === 'uploading' && uploadProgress > 0 && (
                  <div className="w-64 mx-auto mt-3">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300 ease-out"
                        style={{
                          width: `${uploadProgress}%`,
                          background: 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)',
                        }}
                      />
                    </div>
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-2">{t('jobDescription.audioModal.processing.backgroundHint')}</p>
                <p className="text-sm text-gray-500">{t('jobDescription.audioModal.processing.notifyHint')}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-4 py-2 rounded-full">
                <Clock size={14} />
                <span>{t('jobDescription.audioModal.processing.timeHint')}</span>
              </div>
            </div>
          )}

          {/* Success / Result */}
          {status === 'done' && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="text-amber-500 shrink-0" size={18} />
                <p className="text-sm font-medium text-amber-700">{result.disclaimer || t('jobDescription.audioModal.result.disclaimer')}</p>
              </div>
              {result.transcript && (
                <div>
                  <button onClick={() => setShowTranscript(!showTranscript)} className="flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                    {showTranscript ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showTranscript ? t('jobDescription.audioModal.result.hideTranscript') : t('jobDescription.audioModal.result.showTranscript')}
                  </button>
                  {showTranscript && (
                    <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-xl max-h-40 overflow-y-auto">
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{result.transcript}</p>
                    </div>
                  )}
                </div>
              )}
              {result.structured_description && (
                <div className="space-y-3">
                  <h3 className="text-base font-bold text-gray-900">{t('jobDescription.audioModal.result.generatedTitle')}</h3>
                  <div className="p-5 border border-gray-200 rounded-xl bg-white space-y-4 max-h-[40vh] overflow-y-auto">
                    <div>
                      <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.title')}</span>
                      <p className="text-lg font-bold text-gray-900 mt-0.5">{result.structured_description.title}</p>
                    </div>
                    {previewCompanyName && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.company')}</span>
                        <p className="text-sm text-gray-700 mt-0.5">{previewCompanyName}</p>
                      </div>
                    )}
                    {result.structured_description.about_role && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.about')}</span>
                        <p className="text-sm text-gray-700 mt-0.5">{result.structured_description.about_role}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {result.structured_description.experience_level && <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{result.structured_description.experience_level}</span>}
                      {result.structured_description.work_type && <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">{result.structured_description.work_type?.replace('_', ' ')}</span>}
                      {result.structured_description.employment_type && <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">{result.structured_description.employment_type?.replace('_', ' ')}</span>}
                      {result.structured_description.location && <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">📍 {result.structured_description.location}</span>}
                    </div>
                    {result.structured_description.key_responsibilities?.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.responsibilities')}</span>
                        <ul className="mt-1 space-y-1">{result.structured_description.key_responsibilities.map((item, i) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-purple-400 mt-0.5">•</span>{item}</li>)}</ul>
                      </div>
                    )}
                    {result.structured_description.required_qualifications?.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.qualifications')}</span>
                        <ul className="mt-1 space-y-1">{result.structured_description.required_qualifications.map((item, i) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-green-400 mt-0.5">•</span>{item}</li>)}</ul>
                      </div>
                    )}
                    {result.structured_description.nice_to_have_skills?.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.niceToHave')}</span>
                        <ul className="mt-1 space-y-1">{result.structured_description.nice_to_have_skills.map((item, i) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-blue-400 mt-0.5">•</span>{item}</li>)}</ul>
                      </div>
                    )}
                    {result.structured_description.required_skills_list?.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t('jobDescription.audioModal.result.fields.skills')}</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {result.structured_description.required_skills_list.map((s, i) => <span key={i} className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium border border-purple-200">{s}</span>)}
                          {result.structured_description.nice_to_have_skills_list?.map((s, i) => <span key={`n${i}`} className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium border border-blue-200">{s}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-between gap-3">
          <button onClick={handleClose} className="px-5 py-2.5 text-gray-700 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-semibold">
            {isProcessing ? t('jobDescription.audioModal.buttons.closeBackground') : t('jobDescription.audioModal.buttons.cancel')}
          </button>
          <div className="flex items-center gap-3">
            {status === 'done' && (
              <button onClick={() => { reset(); setActiveTab('audio'); }} className="px-5 py-2.5 text-purple-600 border-2 border-purple-200 rounded-xl hover:bg-purple-50 transition-all font-semibold">
                {t('jobDescription.audioModal.buttons.startOver')}
              </button>
            )}
            {status === 'done' ? (
              <button onClick={handleApply} className="px-6 py-2.5 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}>
                {t('jobDescription.audioModal.buttons.useThisJd')}
              </button>
            ) : isProcessing ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancelProcess}
                  className="px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  {t('jobDescription.audioModal.buttons.stopProcess')}
                </button>
                <div className="flex items-center gap-2 px-5 py-2.5 text-purple-600 bg-purple-50 rounded-xl font-semibold">
                  <Loader2 size={16} className="animate-spin" /> {t('jobDescription.audioModal.buttons.running')}
                </div>
              </div>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={activeTab === 'audio' ? !audioFile : !transcriptText.trim()}
                className={`px-6 py-2.5 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl ${(activeTab === 'audio' ? !audioFile : !transcriptText.trim()) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}
              >
                {activeTab === 'audio' ? t('jobDescription.audioModal.buttons.transcribeAndGenerate', 'Transcribe & Generate JD') : t('jobDescription.audioModal.buttons.generateJd', 'Generate JD')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
